import { useMemo, useRef } from "react";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";

import { FilterToolTipContent } from "Components/Generic/FilterToolTipContent";
import { useResizeObserver } from "Hooks/UseResizeObserver";
import { WithTranslator } from "Components/Generic/Translator";
import { hasNoElements, toSafeArray } from "Utils/Array";
import { fitTextListToContent } from "Utils/String";
import { getTranslatedFieldLabel } from "Utils/Field";
import { EntityType } from "Model/EntityType";
import { IRendererProps } from "../IRendererProps";
import { TooltipCloseDelay, TooltipDirection, TooltipOpenDelay } from "../TooltipSettings";
import { useOptionListRendererStyles } from "./UseOptionListRendererStyles";

export const OptionListRenderer = WithTranslator((props: IRendererProps): JSX.Element => {
  const {
    fieldName, separator, item, styles, translator
  } = props;
  const { styleSet, font } = useOptionListRendererStyles(styles);
  const ref = useRef<HTMLDivElement>();
  const width = useResizeObserver(ref);

  const entries = toSafeArray(item?.[fieldName] as string | string[]);
  const [content, tooltipContent] = useMemo(() => {
    if (ref.current == null || hasNoElements(entries)) {
      return [null, null];
    }

    return [
      fitTextListToContent(
        entries.map((entry) => getTranslatedFieldLabel(`${fieldName}.option.${entry}`, EntityType.Project, translator, () => ``)),
        width - 5,
        font,
        separator
      ),
      <ul className={styleSet.tooltip}>
        {entries.map((entry, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            <FilterToolTipContent
              fieldName={fieldName}
              value={getTranslatedFieldLabel(`${fieldName}.option.${entry}`, EntityType.Project, translator)}
              rawValue={entry}
            />
          </li>
        ))}
      </ul>
    ];
  }, [entries, width, font, separator, styleSet.tooltip, fieldName, translator]);

  if (hasNoElements(entries)) {
    return null;
  }

  return (
    <TooltipHost
      hostClassName={styleSet.host}
      closeDelay={TooltipCloseDelay}
      content={tooltipContent}
      delay={TooltipOpenDelay}
      directionalHint={TooltipDirection}
    >
      <div className={styleSet.root} ref={ref}>
        <Text className={styleSet.text}>{content}</Text>
      </div>
    </TooltipHost>
  );
});
